import React, { useState, useEffect } from 'react';
import { serviceAPI, packageAPI } from '../lib/api';
import { formatINR, formatPercent, apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Package,
  Clock,
  Users,
  Loader2
} from 'lucide-react';

const CATEGORIES = ['Creative', 'Video', 'Shoot', 'Planning', 'Branding', 'Marketing'];
const DEPARTMENTS = ['Branding', 'Marketing', 'Operations', 'Sales', 'Video Editing', 'Designing', 'Video Production', 'Advertising'];

export default function Services() {
  const { can } = useAuth();
  const canWrite = can('write');
  const canDelete = can('delete');
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('services');
  
  // Service dialog state
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [serviceForm, setServiceForm] = useState({
    name: '',
    category: '',
    avg_time_hours: '',
    primary_department: '',
    secondary_departments: [],
    team_size: '1',
    base_cost: '',
    markup_percentage: '30',
    landing_price: '',
    selling_price: ''
  });

  // Package dialog state
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  
  const [packageForm, setPackageForm] = useState({
    name: '',
    description: '',
    components: [],
    landing_cost: '',
    selling_price: '',
    margin_percentage: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, packagesRes] = await Promise.all([
        serviceAPI.getAll(),
        packageAPI.getAll()
      ]);
      setServices(servicesRes.data);
      setPackages(packagesRes.data);
    } catch (error) {
      toast.error(apiError(error, 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  };

  // Service form handlers
  const resetServiceForm = () => {
    setServiceForm({
      name: '',
      category: '',
      avg_time_hours: '',
      primary_department: '',
      secondary_departments: [],
      team_size: '1',
      base_cost: '',
      markup_percentage: '30',
      landing_price: '',
      selling_price: ''
    });
    setEditingService(null);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name,
      category: service.category,
      avg_time_hours: service.avg_time_hours.toString(),
      primary_department: service.primary_department,
      secondary_departments: service.secondary_departments || [],
      team_size: service.team_size.toString(),
      base_cost: service.base_cost.toString(),
      markup_percentage: service.markup_percentage.toString(),
      landing_price: service.landing_price.toString(),
      selling_price: service.selling_price.toString()
    });
    setServiceDialogOpen(true);
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...serviceForm,
        avg_time_hours: parseFloat(serviceForm.avg_time_hours),
        team_size: parseInt(serviceForm.team_size),
        base_cost: parseFloat(serviceForm.base_cost) || 0,
        markup_percentage: parseFloat(serviceForm.markup_percentage),
        landing_price: parseFloat(serviceForm.landing_price) || 0,
        selling_price: parseFloat(serviceForm.selling_price) || 0
      };

      // Auto-calculate if not provided
      if (!data.landing_price) {
        data.landing_price = data.base_cost;
      }
      if (!data.selling_price) {
        data.selling_price = data.base_cost * (1 + data.markup_percentage / 100);
      }

      if (editingService) {
        await serviceAPI.update(editingService.id, data);
        toast.success('Service updated successfully');
      } else {
        await serviceAPI.create(data);
        toast.success('Service added successfully');
      }

      setServiceDialogOpen(false);
      resetServiceForm();
      fetchData();
    } catch (error) {
      toast.error(apiError(error, 'Failed to save service'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      await serviceAPI.delete(id);
      toast.success('Service deleted');
      fetchData();
    } catch (error) {
      toast.error(apiError(error, 'Failed to delete service'));
    }
  };

  // Package form handlers
  const resetPackageForm = () => {
    setPackageForm({
      name: '',
      description: '',
      components: [],
      landing_cost: '',
      selling_price: '',
      margin_percentage: ''
    });
    setEditingPackage(null);
  };

  const handleEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name,
      description: pkg.description || '',
      components: pkg.components || [],
      landing_cost: pkg.landing_cost.toString(),
      selling_price: pkg.selling_price.toString(),
      margin_percentage: pkg.margin_percentage.toString()
    });
    setPackageDialogOpen(true);
  };

  const handlePackageSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...packageForm,
        landing_cost: parseFloat(packageForm.landing_cost) || 0,
        selling_price: parseFloat(packageForm.selling_price) || 0,
        margin_percentage: parseFloat(packageForm.margin_percentage) || 0
      };

      // Calculate margin
      if (data.selling_price > 0 && data.landing_cost > 0) {
        data.margin_percentage = ((data.selling_price - data.landing_cost) / data.selling_price) * 100;
      }

      if (editingPackage) {
        await packageAPI.update(editingPackage.id, data);
        toast.success('Package updated successfully');
      } else {
        await packageAPI.create(data);
        toast.success('Package created successfully');
      }

      setPackageDialogOpen(false);
      resetPackageForm();
      fetchData();
    } catch (error) {
      toast.error(apiError(error, 'Failed to save package'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePackage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    
    try {
      await packageAPI.delete(id);
      toast.success('Package deleted');
      fetchData();
    } catch (error) {
      toast.error(apiError(error, 'Failed to delete package'));
    }
  };

  const addComponentToPackage = () => {
    setPackageForm(prev => ({
      ...prev,
      components: [...prev.components, { service_id: '', service_name: '', quantity: 1 }]
    }));
  };

  const updateComponent = (index, field, value) => {
    setPackageForm(prev => {
      const newComponents = [...prev.components];
      newComponents[index] = { ...newComponents[index], [field]: value };
      
      // If service is selected, auto-fill name
      if (field === 'service_id') {
        const service = services.find(s => s.id === value);
        if (service) {
          newComponents[index].service_name = service.name;
        }
      }
      
      return { ...prev, components: newComponents };
    });
  };

  const removeComponent = (index) => {
    setPackageForm(prev => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Services & Packages</h1>
        <p className="text-gray-500 mt-1">Configure your service offerings and pricing</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="services" data-testid="services-tab">Services</TabsTrigger>
            <TabsTrigger value="packages" data-testid="packages-tab">Packages</TabsTrigger>
          </TabsList>
          
          {activeTab === 'services' ? (
            <Dialog open={serviceDialogOpen} onOpenChange={(open) => { setServiceDialogOpen(open); if (!open) resetServiceForm(); }}>
              {canWrite && (
                <DialogTrigger asChild>
                  <Button data-testid="add-service-btn" className="bg-black hover:bg-black/90">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Service
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleServiceSubmit} className="space-y-6 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="serviceName">Service Name *</Label>
                      <Input
                        id="serviceName"
                        data-testid="service-name-input"
                        value={serviceForm.name}
                        onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Select 
                        value={serviceForm.category} 
                        onValueChange={(v) => setServiceForm({ ...serviceForm, category: v })}
                      >
                        <SelectTrigger data-testid="service-category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="avgTime">Avg. Time (hours) *</Label>
                      <Input
                        id="avgTime"
                        type="number"
                        step="0.5"
                        data-testid="service-time-input"
                        value={serviceForm.avg_time_hours}
                        onChange={(e) => setServiceForm({ ...serviceForm, avg_time_hours: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="teamSize">Team Size *</Label>
                      <Input
                        id="teamSize"
                        type="number"
                        min="1"
                        data-testid="service-team-input"
                        value={serviceForm.team_size}
                        onChange={(e) => setServiceForm({ ...serviceForm, team_size: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="primaryDept">Primary Department *</Label>
                      <Select 
                        value={serviceForm.primary_department} 
                        onValueChange={(v) => setServiceForm({ ...serviceForm, primary_department: v })}
                      >
                        <SelectTrigger data-testid="service-dept-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {DEPARTMENTS.map(dept => (
                            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="baseCost">Base Cost (₹)</Label>
                      <Input
                        id="baseCost"
                        type="number"
                        data-testid="service-cost-input"
                        value={serviceForm.base_cost}
                        onChange={(e) => setServiceForm({ ...serviceForm, base_cost: e.target.value })}
                        placeholder="Auto-calculated"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="markup">Markup % *</Label>
                      <Input
                        id="markup"
                        type="number"
                        data-testid="service-markup-input"
                        value={serviceForm.markup_percentage}
                        onChange={(e) => setServiceForm({ ...serviceForm, markup_percentage: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sellingPrice">Selling Price (₹)</Label>
                      <Input
                        id="sellingPrice"
                        type="number"
                        data-testid="service-price-input"
                        value={serviceForm.selling_price}
                        onChange={(e) => setServiceForm({ ...serviceForm, selling_price: e.target.value })}
                        placeholder="Auto-calculated"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setServiceDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving} className="bg-black hover:bg-black/90">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingService ? 'Update' : 'Add Service'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          ) : (
            <Dialog open={packageDialogOpen} onOpenChange={(open) => { setPackageDialogOpen(open); if (!open) resetPackageForm(); }}>
              {canWrite && (
                <DialogTrigger asChild>
                  <Button data-testid="add-package-btn" className="bg-black hover:bg-black/90">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Package
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingPackage ? 'Edit Package' : 'Create New Package'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePackageSubmit} className="space-y-6 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="packageName">Package Name *</Label>
                      <Input
                        id="packageName"
                        data-testid="package-name-input"
                        value={packageForm.name}
                        onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="packageDesc">Description</Label>
                      <Input
                        id="packageDesc"
                        data-testid="package-desc-input"
                        value={packageForm.description}
                        onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Package Components</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addComponentToPackage}>
                        <Plus className="w-4 h-4 mr-1" /> Add Service
                      </Button>
                    </div>
                    
                    {packageForm.components.map((comp, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Select 
                          value={comp.service_id} 
                          onValueChange={(v) => updateComponent(index, 'service_id', v)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select service" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map(svc => (
                              <SelectItem key={svc.id} value={svc.id}>{svc.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          min="1"
                          value={comp.quantity}
                          onChange={(e) => updateComponent(index, 'quantity', parseInt(e.target.value))}
                          className="w-20"
                          placeholder="Qty"
                        />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeComponent(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="landingCost">Landing Cost (₹) *</Label>
                      <Input
                        id="landingCost"
                        type="number"
                        data-testid="package-cost-input"
                        value={packageForm.landing_cost}
                        onChange={(e) => setPackageForm({ ...packageForm, landing_cost: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pkgSellingPrice">Selling Price (₹) *</Label>
                      <Input
                        id="pkgSellingPrice"
                        type="number"
                        data-testid="package-price-input"
                        value={packageForm.selling_price}
                        onChange={(e) => setPackageForm({ ...packageForm, selling_price: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pkgMargin">Margin %</Label>
                      <Input
                        id="pkgMargin"
                        type="number"
                        value={packageForm.margin_percentage}
                        onChange={(e) => setPackageForm({ ...packageForm, margin_percentage: e.target.value })}
                        placeholder="Auto-calculated"
                        disabled
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setPackageDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving} className="bg-black hover:bg-black/90">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingPackage ? 'Update' : 'Create Package'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Services Tab */}
        <TabsContent value="services" className="mt-6">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th className="table-header text-left">Service</th>
                      <th className="table-header text-left">Category</th>
                      <th className="table-header text-left">Department</th>
                      <th className="table-header text-center">Time</th>
                      <th className="table-header text-center">Team</th>
                      <th className="table-header text-right">Base Cost</th>
                      <th className="table-header text-right">Selling Price</th>
                      <th className="table-header text-center">Markup</th>
                      {(canWrite || canDelete) && <th className="table-header text-center">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {services.length > 0 ? (
                      services.map((service) => (
                        <tr key={service.id} className="table-row" data-testid={`service-row-${service.id}`}>
                          <td className="table-cell font-medium max-w-[260px] truncate" title={service.name}>{service.name}</td>
                          <td className="table-cell">
                            <span className="badge badge-gray">{service.category}</span>
                          </td>
                          <td className="table-cell text-gray-600">{service.primary_department}</td>
                          <td className="table-cell text-center">
                            <div className="flex items-center justify-center gap-1 text-gray-600">
                              <Clock className="w-3 h-3" />
                              {service.avg_time_hours}h
                            </div>
                          </td>
                          <td className="table-cell text-center">
                            <div className="flex items-center justify-center gap-1 text-gray-600">
                              <Users className="w-3 h-3" />
                              {service.team_size}
                            </div>
                          </td>
                          <td className="table-cell text-right font-mono">{formatINR(service.base_cost)}</td>
                          <td className="table-cell text-right font-mono font-medium">{formatINR(service.selling_price)}</td>
                          <td className="table-cell text-center">
                            <span className="badge badge-success">{service.markup_percentage}%</span>
                          </td>
                          {(canWrite || canDelete) && (
                            <td className="table-cell">
                              <div className="flex items-center justify-center gap-2">
                                {canWrite && (
                                  <Button variant="ghost" size="sm" onClick={() => handleEditService(service)} aria-label={`Edit ${service.name}`} title="Edit">
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                )}
                                {canDelete && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteService(service.id)}
                                    className="text-red-700 hover:text-red-700 hover:bg-red-50"
                                    aria-label={`Delete ${service.name}`}
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={canWrite || canDelete ? 9 : 8} className="text-center py-12 text-gray-400">
                          No services configured yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Packages Tab */}
        <TabsContent value="packages" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.length > 0 ? (
              packages.map((pkg) => (
                <Card key={pkg.id} className="card-hover" data-testid={`package-card-${pkg.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <Package className="w-5 h-5" />
                        </div>
                        <CardTitle className="text-lg">{pkg.name}</CardTitle>
                      </div>
                      <div className="flex gap-1">
                        {canWrite && (
                          <Button variant="ghost" size="sm" onClick={() => handleEditPackage(pkg)} aria-label={`Edit ${pkg.name}`} title="Edit">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePackage(pkg.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            aria-label={`Delete ${pkg.name}`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {pkg.description && (
                      <p className="text-sm text-gray-500 mb-4">{pkg.description}</p>
                    )}
                    
                    {pkg.components?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Includes:</p>
                        <div className="space-y-1">
                          {pkg.components.map((comp, idx) => (
                            <div key={idx} className="text-sm flex justify-between">
                              <span>{comp.service_name}</span>
                              <span className="text-gray-500">x{comp.quantity}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center pt-4 border-t">
                      <div>
                        <p className="text-xs text-gray-500">Landing Cost</p>
                        <p className="font-mono">{formatINR(pkg.landing_cost)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Selling Price</p>
                        <p className="font-mono font-bold">{formatINR(pkg.selling_price)}</p>
                      </div>
                    </div>
                    
                    {pkg.margin_percentage > 0 && (
                      <div className="mt-2 text-center">
                        <span className={`badge ${
                          pkg.margin_percentage >= 30 ? 'badge-success' :
                          pkg.margin_percentage >= 20 ? 'badge-warning' : 'badge-error'
                        }`}>
                          {formatPercent(pkg.margin_percentage)} margin
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center py-12 text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No packages created yet</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
